import React, { useState } from 'react';
import { Form, Button, InputNumber, Select, Row, Col } from 'antd';
import QueryBuilderInput from 'components/QueryBuilder/QueryBuilderInput';
import Table from 'components/Table';
import RemoveInput from 'components/RemoveInput';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DrawerForm from 'components/DrawerForm';
import MetricConfig from './MetricConfig';
import DimensionConfig from './DimensionConfig';

const LabelColumn = ({ value, getLabel }) => getLabel(value);

const getMetricsColumns = (metrics) => [{
  title: 'Metric',
  dataIndex: 'name',
  render: (_, field) => (
    <Form.Item name={[field.name, 'name']}>
      <LabelColumn getLabel={name => metrics?.find(m => m.name === name)?.label || name} />
    </Form.Item>
  ),
}];

const getDimensionsColumns = (dimensions) => [{
  title: 'Dimension',
  dataIndex: 'name',
  render: (_, field) => (
    <Form.Item name={[field.name, 'name']}>
      <LabelColumn getLabel={name => dimensions?.find(d => d.name === name)?.label || name} />
    </Form.Item>
  ),
}];

const EditButton = ({ value, onEdit }) => (
  <Button key="edit" icon={<EditOutlined />} onClick={() => onEdit(value)}>Edit</Button>
);

const ResultsConfig = () => {
  const [editMetric, setEditMetric] = useState(null);
  const [addMetric, setAddMetric] = useState(null);
  const [editDimension, setEditDimension] = useState(null);
  const [addDimension, setAddDimension] = useState(null);

  const metrics = Form.useWatch('metrics');
  const dimensions = Form.useWatch('dimensions');
  const form = Form.useFormInstance();

  const metricOptions = metrics?.map(m => ({ label: m.label, value: m.name })) || [];

  return (
    <>
      <Form.Item>
        <Form.Item>
          <Form.List name="dimensions">
            {(rows, { remove, move }) => (
              <>
                <Table
                  emptyMessage="No dimensions"
                  pagination={false}
                  onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                  columns={getDimensionsColumns(dimensions)}
                  dataSource={rows}
                  rowKey="name"
                  interactive={false}
                  actions={field => (
                    <>
                      <Form.Item name={[field.name, 'name']} noStyle>
                        <EditButton onEdit={setEditDimension} />
                      </Form.Item>
                      <RemoveInput onRemove={() => remove(field.name)} />
                    </>
                  )}
                />
                <Button style={{ marginTop: 16 }} onClick={() => setAddDimension(true)} icon={<PlusOutlined />}>Add dimension</Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.List name="metrics">
          {(rows, { remove, move }) => (
            <>
              <Table
                emptyMessage="No metrics"
                pagination={false}
                onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                columns={getMetricsColumns(metrics)}
                dataSource={rows}
                rowKey="key"
                interactive={false}
                actions={field => (
                  <>
                    <Form.Item name={[field.name, 'name']} noStyle>
                      <EditButton onEdit={setEditMetric} />
                    </Form.Item>
                    <RemoveInput onRemove={() => remove(field.name)} />
                  </>
                )}
              />
              <Button style={{ marginTop: 16 }} onClick={() => setAddMetric(true)} icon={<PlusOutlined />}>Add metric</Button>
            </>
          )}
        </Form.List>
      </Form.Item>
      <Row>
        <Col>
          <Form.Item name={['settings', 'rowLimit']} label="Maximum rows">
            <InputNumber min={1} max={1000} />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name={['settings', 'pageLimit']} label="Rows per page">
            <Select options={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: 'All', value: null },
            ]}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="filter" label="Filter">
        <QueryBuilderInput />
      </Form.Item>
      <DrawerForm
        title="Edit metric"
        open={!!editMetric}
        onCancel={() => setEditMetric(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'metrics',
            touched: true,
            value: metrics.map(m => (m.name === editMetric ? values : m)),
          }]);
          setEditMetric(null);
        }}
        initialValues={metrics?.find(m => m?.name === editMetric)}
        successMessage={false}
      >
        <MetricConfig metrics={metricOptions} />
      </DrawerForm>
      <DrawerForm
        title="Add metric"
        open={!!addMetric}
        onCancel={() => setAddMetric(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'metrics',
            touched: true,
            value: [...metrics, values],
          }]);
          setAddMetric(null);
        }}
        successMessage={false}
        initialValues={{
          displayConfig: {
            visible: true,
            columns: ['value'],
            format: 'number',
          },
        }}
      >
        <MetricConfig metrics={metricOptions} />
      </DrawerForm>
      <DrawerForm
        title="Edit dimension"
        open={!!editDimension}
        onCancel={() => setEditDimension(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'dimensions',
            touched: true,
            value: dimensions.map(d => (d.name === editDimension ? values : d)),
          }]);
          setEditDimension(null);
        }}
        initialValues={dimensions?.find(d => d.name === editDimension)}
        successMessage={false}
      >
        <DimensionConfig />
      </DrawerForm>
      <DrawerForm
        title="Add dimension"
        open={!!addDimension}
        onCancel={() => setAddDimension(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'dimensions',
            touched: true,
            value: [...dimensions, values],
          }]);
          setAddDimension(null);
        }}
        successMessage={false}
      >
        <DimensionConfig />
      </DrawerForm>
    </>
  );
};

export default ResultsConfig;
